/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/calendar.svg";
import __asset_1 from "@res/icons/icon_arrow_down_with_circle.svg";
import {COLORS} from "@res/colors";
import * as React from "react";
import {BaseComponent, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {Card, CARD_BORDER_RADIUS} from "../../widget/Card";
import {CardHeader} from "../../widget/CardHeader";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {VGap} from "../../widget/VGap";
import {check_icon, clock_icon, metro_plus_logo} from "@res/icons";
import {DB_Patient} from "@app/app-shared/patient";
import {Calendar} from "../messages/Calendar";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {AddAnniversaryRequest, Api_AddAnniversary, Api_Deactivate, Api_GetSurveyStatus, DeactivateRequest, SurveyStatus} from "@app/app-shared/metro";
import {jsPDF} from 'jspdf';
import dayjs from "../../../utils/dayjs";
import {ContactsMap} from "../messages/ContactsContext";
import {Gender} from "@app/ir-q-app-common/types/contacts";
import {demoChapsSurveyStatuses, demoHraSurveyStatuses} from "./survey-demo-data";

const BodyCSS = emotion.css(
    {
        flex: 1,
        overflow: "auto"
    }
);

const cardHeader = emotion.css(
    {
        height: 50,
        padding: 0
    }
);

const tableHeaderCSS = emotion.css({
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    position: "relative",
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "2.5%",
        width: "95%",
        height: "1px",
        backgroundColor: `${COLORS.grays_2}`
    }
});

const tableRowWithoutBorderCSS = emotion.css({
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    position: "relative",
    "&:hover": {
        backgroundColor: COLORS.very_light_blue
    }
});

const tableRowCSS = emotion.css({
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    position: "relative",
    "&:hover": {
        backgroundColor: COLORS.very_light_blue
    },
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "2.5%",
        width: "95%",
        height: "1px",
        backgroundColor: `${COLORS.grays_2}`
    }
});

const columnCSS = emotion.css({
    flex: 1,
    minWidth: 0
});

const smallColumnCSS = emotion.css({
    flex: 0.5,
    minWidth: 0
});

const statusCompletedCSS = emotion.css({
    color: COLORS.sap_green,
    fontWeight: 500,
    fontFamily: 'Inter-Regular'
});

const statusProgressCSS = emotion.css({
    color: COLORS.macaroni_and_cheese,
    fontWeight: 500,
    fontFamily: 'Inter-Regular'
});

const statusNotStartedCSS = emotion.css({
    color: COLORS.steelGray,
    fontWeight: 500,
    fontFamily: 'Inter-Regular'
});

const statusDeactivatedCSS = emotion.css({
    color: COLORS.steelGray,
    fontWeight: 500,
    fontFamily: 'Inter-Regular'
});

const anniversaryDateCSS = emotion.css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0px 16px",
    backgroundColor: COLORS.macaroni_and_cheese_light,
    height: "40px !important"
});

const datePickerCSS = emotion.css({
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": {
        borderColor: COLORS.very_light_blue
    }
});

const threeDotsButtonCSS = emotion.css({
    padding: "8px",
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    border: "none",
    "&:hover": {
        backgroundColor: COLORS.very_light_blue,
        borderRadius: "4px"
    }
});

const modalContentCSS = emotion.css({
    position: "fixed",
    backgroundColor: COLORS.white,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    padding: "8px 0",
    minWidth: "120px",
    zIndex: 1000
});

const modalOptionCSS = emotion.css({
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    "&:hover": {
        backgroundColor: COLORS.very_light_blue
    }
});

const calendarModalOverlayCSS = emotion.css({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1001
});

const calendarModalContentCSS = emotion.css({
    position: "fixed",
    backgroundColor: COLORS.white,
    borderRadius: "8px",
    padding: "12px",
    maxWidth: "280px",
    width: "280px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    zIndex: 9999,
    fontFamily: "Inter-Regular",
    "& table": {
        fontSize: "12px",
        width: "100%",
        fontFamily: "Inter-Regular"
    },
    "& th, & td": {
        padding: "4px",
        fontSize: "12px",
        fontFamily: "Inter-Regular"
    }
});

type State = {
    hraSurveyStatusItems: SurveyStatus[];
    chapsSurveyStatusItems: SurveyStatus[];
    showModal: boolean;
    modalPosition: { x: number; y: number };
    selectedItem: SurveyStatus | null;
    showCalendar: boolean;
    calendarPosition: { x: number; y: number };
    isHraLoading: boolean;
    isChapsLoading: boolean;
    survey: 'chaps' | 'hra';
}

type Props = {
    patient: DB_Patient;
    contacts: ContactsMap;
    demo?: boolean;
};

export class SurveyStatusPanel
    extends BaseComponent<Props, State> {

    private totalHraQuestions: number = 25;
    private totalChapsQuestions: number = 20;

    constructor(props: Props) {
        super(props);
        this.state = {
            hraSurveyStatusItems: [],
            chapsSurveyStatusItems: [],
            showModal: false,
            modalPosition: { x: 0, y: 0 },
            selectedItem: null,
            showCalendar: false,
            calendarPosition: { x: 0, y: 0 },
            isHraLoading: true,
            isChapsLoading: true,
            survey: 'hra'
        };
    }

    componentDidUpdate(prevProps: Props) {
        const patientChanged = prevProps.patient.unitId !== this.props.patient.unitId;
        const demoChanged = prevProps.demo !== this.props.demo;
        if (!patientChanged && !demoChanged)
            return;

        if (this.props.demo) {
            this.loadMockData();
            return;
        }

        this.setState({
            hraSurveyStatusItems: [],
            chapsSurveyStatusItems: [],
            showModal: false,
            selectedItem: null,
            showCalendar: false,
            isHraLoading: true,
            isChapsLoading: true
        }, () => {
            this.totalHraQuestions = this.calculateTotalQuestions();
            this.fetchData(this.props.patient.unitId);
        });
    }

    componentDidMount() {
        this.totalHraQuestions = this.calculateTotalQuestions();
        if (this.props.demo) {
            this.loadMockData();
        } else {
            this.fetchData(this.props.patient.unitId);
        }
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('scroll', this.handleScroll, true);
    }

    private loadMockData = () => {
        this.setState({
            hraSurveyStatusItems: demoHraSurveyStatuses,
            chapsSurveyStatusItems: demoChapsSurveyStatuses,
            isHraLoading: false,
            isChapsLoading: false
        });
    };

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('scroll', this.handleScroll, true);
    }

    calculateTotalQuestions = (): number => {
        const { patient, contacts } = this.props;
        const contact = contacts[patient.contactId || ""];

        if (!contact?.birthday || !contact.gender) {
            return 25; // Default fallback
        }

        const age = dayjs().diff(dayjs(contact.birthday), 'year'); // dayjs units are singular ('year', not 'years')

        if (contact.gender === Gender.Male) {
            // Men: Under 50 or above 75 = 25 questions, Between 50-75 = 26 questions
            if (age < 50 || age > 75) {
                return 25;
            } else {
                return 26;
            }
        } else if (contact.gender === Gender.Female) {
            // Women: Under 50 or above 75 = 25 questions, Age 75 = 26 questions, Age 50-74 = 27 questions
            if (age < 50 || age > 75) {
                return 25;
            } else if (age === 75) {
                return 26;
            } else {
                return 27; // age 50-74
            }
        }

        return 25; // Default fallback
    };

    handleScroll = () => {
        if (this.state.showModal) {
            this.setState({ showModal: false, selectedItem: null });
        }
    };

    handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        // Check if click is outside modal and not on the three dots button
        if (this.state.showModal && !target.closest('.modal-content') && !target.closest('.three-dots-button')) {
            this.setState({ showModal: false, selectedItem: null });
        }
    };

    fetchData = (unitId: string) => {
        XhrHttpModule
            .createRequest<Api_GetSurveyStatus>(HttpMethod.POST, "get-hra-survey-statuses")
            .setRelativeUrl("/v1/metro/survey-status")
            .setJsonBody({unitId})
            .setOnError("Failed to retrieve HRA survey statuses")
            .execute(response => {
                this.setState({hraSurveyStatusItems: response.hra_statuses, isChapsLoading: false, isHraLoading: false, chapsSurveyStatusItems: response.chaps_statuses})
            });
    };


    handleThreeDotsClick = (event: React.MouseEvent, item: SurveyStatus) => {
        event.stopPropagation();
        const rect = (event.target as HTMLElement).closest('button')!.getBoundingClientRect();
        this.setState({
            showModal: !this.state.showModal || this.state.selectedItem !== item,
            modalPosition: { x: rect.left - 120, y: rect.bottom - 25 }, // Position to the left
            selectedItem: item
        });
    };

    handleDeactivateToggle = () => {
        const { selectedItem } = this.state;
        if (!selectedItem) return;

        const newDeactivatedValue = !selectedItem.deactivated;

        if (this.props.demo) {
            this.setState(prevState => {
                const key = this.state.survey === 'hra' ? 'hraSurveyStatusItems' : 'chapsSurveyStatusItems';
                return {
                    [key]: prevState[key].map(item =>
                        item === selectedItem ? {...item, deactivated: newDeactivatedValue} : item
                    ),
                    showModal: false,
                    selectedItem: null
                } as Pick<State, typeof key | 'showModal' | 'selectedItem'>;
            });
            return;
        }

        const requestBody: DeactivateRequest = {
            unitId: this.props.patient.unitId,
            deactivated: newDeactivatedValue,
            year: selectedItem.year,
            surveyType: this.state.survey
        };

        // Add year when activating
        if (!newDeactivatedValue) {
            requestBody.year = selectedItem.year;
        }

        XhrHttpModule
            .createRequest<Api_Deactivate>(HttpMethod.POST, "toggle-survey-status")
            .setRelativeUrl("/v1/metro/deactivate") // Adjust URL as needed
            .setJsonBody(requestBody)
            .setOnError("Failed to update survey status")
            .execute(() => {
                // Update the item in state immediately
                this.setState(prevState => {
                    const updatedState = {
                        ...prevState,
                        showModal: false,
                        selectedItem: null
                    };

                    // Update the appropriate survey array
                    if (this.state.survey === 'hra') {
                        updatedState.hraSurveyStatusItems = prevState.hraSurveyStatusItems.map(item =>
                            item === selectedItem
                                ? { ...item, deactivated: newDeactivatedValue }
                                : item
                        );
                    } else {
                        updatedState.chapsSurveyStatusItems = prevState.chapsSurveyStatusItems.map(item =>
                            item === selectedItem
                                ? { ...item, deactivated: newDeactivatedValue }
                                : item
                        );
                    }

                    return updatedState;
                });
            });
    };

    handleDateClick = (event: React.MouseEvent) => {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const calendarWidth = 280; // Updated width from CSS
        const calendarHeight = 320; // Smaller height estimate
        const margin = 20;

        // Calculate initial position
        let x = rect.right + 10;
        let y = rect.top - 10;

        // Check if calendar would go off the right edge of screen
        if (x + calendarWidth > window.innerWidth - margin) {
            x = rect.left - calendarWidth - 10; // Position to the left instead
        }

        // Still off the left edge? Center it horizontally
        if (x < margin) {
            x = Math.max(margin, (window.innerWidth - calendarWidth) / 2);
        }

        // Check if calendar would go off the bottom edge of screen
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < calendarHeight + margin) {
            if (spaceAbove > calendarHeight + margin) {
                // Position above the date picker
                y = rect.top - calendarHeight - 10;
            } else {
                // Not enough space above or below, position as high as possible
                y = Math.max(margin, window.innerHeight - calendarHeight - margin);
            }
        }

        // Final check to ensure it's within viewport
        if (y < margin) y = margin;
        if (x < margin) x = margin;

        this.setState({
            showCalendar: true,
            calendarPosition: { x, y }
        });
    };

    handleCalendarDateSelect = (selectedDate: Date) => {
        // Create UTC date with same day/month/year as selected date.
        // dayjs.utc() does NOT accept the [y, monthIndex, d] array form moment used
        // (it mis-parses it), so build the UTC midnight instant explicitly.
        const utcTimestamp = Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

        if (this.props.demo) {
            const formattedDate = dayjs.utc(utcTimestamp).format("MM/DD/YYYY");
            this.setState(prevState => ({
                hraSurveyStatusItems: prevState.hraSurveyStatusItems.map(item => ({...item, anniversary: formattedDate})),
                chapsSurveyStatusItems: prevState.chapsSurveyStatusItems.map(item => ({...item, anniversary: formattedDate})),
                showCalendar: false
            }));
            return;
        }

        const requestBody: AddAnniversaryRequest = {
            unitId: this.props.patient.unitId,
            date: utcTimestamp
        };

        XhrHttpModule
            .createRequest<Api_AddAnniversary>(HttpMethod.POST, "update-anniversary-date")
            .setRelativeUrl("/v1/metro/add-anniversary")
            .setJsonBody(requestBody)
            .setOnError("Failed to update anniversary date")
            .execute(() => {
                // Refresh the data to get updated anniversary date
                this.fetchData(this.props.patient.unitId);
                this.setState({ showCalendar: false });
            });
    };

    handleCalendarClose = () => {
        this.setState({ showCalendar: false });
    };

    render() {
        return <Card>
            <div style={{
                display: "flex",
                flexDirection: "row",
                width: '100%'
            }}>
                <CardHeader className={cardHeader}
                            style={{
                                backgroundColor: this.state.survey === 'hra' ? COLORS.macaroni_and_cheese : COLORS.white,
                                justifyContent: "normal",
                                width: '50%',
                                cursor: 'pointer'
                            }}
                            onClick={() => this.setState({ survey: 'hra' })}
                >
                    {this.renderHRACardHeader()}
                </CardHeader>
                <CardHeader className={cardHeader}
                            style={{
                                backgroundColor: this.state.survey === 'chaps' ? COLORS.macaroni_and_cheese : COLORS.white,
                                justifyContent: "normal",
                                width: '50%',
                                cursor: 'pointer'
                            }}
                            onClick={() => this.setState({ survey: 'chaps' })}
                >
                    {this.renderChapsCardHeader()}
                </CardHeader>
            </div>

            <div className={"ll_v_s"} style={{
                overflow: "hidden",
                borderRadius: `0px 0px ${CARD_BORDER_RADIUS} ${CARD_BORDER_RADIUS}`
            }}>
                {this.renderSubCardHeader()}
                <div className={BodyCSS}>
                    {this.renderCardContent()}
                </div>
            </div>
            {this.renderModal()}
            {this.renderCalendarModal()}
        </Card>;
    }

    renderHRACardHeader = () => {
        if (this.state.survey === 'hra')
            return <>
                <div style={{marginLeft: 23}}>
                    {FONTS.medium_18_white("HRA Survey Status")}
                </div>
            </>;
        else
            return <>
                <div style={{marginLeft: 23}}>
                    {FONTS.medium_18_graysdark("HRA Survey Status")}
                </div>
            </>;
    };

    renderChapsCardHeader = () => {
        if (this.state.survey === 'chaps')
            return <>
                <div style={{marginLeft: 23}}>
                    {FONTS.medium_18_white("CAHPS Survey Status")}
                </div>
            </>;
        else
            return <>
                <div style={{marginLeft: 23}}>
                    {FONTS.medium_18_graysdark("CAHPS Survey Status")}
                </div>
            </>;
    };

    renderSubCardHeader = () => {
        const currentSurveyData = this.state.survey === 'hra' ? this.state.hraSurveyStatusItems : this.state.chapsSurveyStatusItems;
        const anniversaryDate = currentSurveyData?.[0]?.anniversary || "--/--/----";
        const surveyType = this.state.survey === 'hra' ? 'HRA' : 'CAHPS';

        return <div className={anniversaryDateCSS}>
            <div>
                {FONTS.reg_14_neutral_text_dark_semi_bold(`Member anniversary date - ${surveyType}`)}
            </div>
            <div className={datePickerCSS} onClick={this.handleDateClick}>
                <img style={{marginRight: "9px"}} src={__asset_0} alt={"Calendar icon"}/>
                <span style={{color: COLORS.primary_dark_blue, marginRight: 16}}>
                    {FONTS.reg_14_blue(anniversaryDate)}
                </span>
                <img src={__asset_1} alt={"Arrow icon to open the calendar"}/>
            </div>
        </div>;
    };

    private renderCardContent = () => {
        if (this.state.survey === 'hra') {
            return this.renderHRACardContent();
        } else {
            return this.renderChapsCardContent();
        }
    };

    private renderHRACardContent = () => {
        const { hraSurveyStatusItems, isHraLoading } = this.state;
        const anniversaryDate = hraSurveyStatusItems?.[0]?.anniversary;
        if (isHraLoading) {
            return <VGap v={100}/>;
        }

        if (!anniversaryDate) {
            return this.renderNoAnniversarySet();
        }

        if (!hraSurveyStatusItems || hraSurveyStatusItems.length === 0)
            return <VGap v={100}/>;

        return <div>
            {this.renderTableHeader()}
            {hraSurveyStatusItems.map((item, index) => {
                return this.renderRow(item, index);
            })}
        </div>;
    };

    private renderChapsCardContent = () => {
        const { chapsSurveyStatusItems, isChapsLoading } = this.state;
        const anniversaryDate = chapsSurveyStatusItems?.[0]?.anniversary;
        if (isChapsLoading) {
            return <VGap v={100}/>;
        }

        if (!anniversaryDate) {
            return this.renderNoAnniversarySet();
        }

        if (!chapsSurveyStatusItems || chapsSurveyStatusItems.length === 0)
            return <VGap v={100}/>;

        return <div>
            {this.renderTableHeader()}
            {chapsSurveyStatusItems.map((item, index) => {
                return this.renderRow(item, index);
            })}
        </div>;
    };

    private renderNoAnniversarySet = () => {
        const surveyType = this.state.survey === 'hra' ? 'HRA' : 'CAHPS';
        return <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
            color: COLORS.neutral_text_dark
        }}>
            <div style={{
                fontSize: '14px',
                fontFamily: 'Inter-Regular',
                fontWeight: 500,
                marginBottom: '8px'
            }}>
                Set the member's anniversary date to activate the {surveyType} survey.
            </div>
            <div style={{
                fontSize: '13px',
                fontFamily: 'Inter-Regular',
                color: COLORS.steelGray
            }}>
                Please note that the survey will begin 120 days prior to this date
            </div>
        </div>;
    };

    private renderTableHeader = () => {
        return <div className={tableHeaderCSS}>
            <div className={smallColumnCSS}>
                {FONTS.light_12("Year")}
            </div>
            <div className={columnCSS}>
                {FONTS.light_12("Survey started")}
            </div>
            <div className={columnCSS}>
                {FONTS.light_12("Status")}
            </div>
            <div className={columnCSS}>
                {FONTS.light_12("Survey completed")}
            </div>
            <div style={{width: 60, textAlign: "center", display: "flex", justifyContent: "center"}}>
                {FONTS.light_12("Action")}
            </div>
        </div>;
    };

    private renderRow = (item: SurveyStatus, index: number) => {
        const currentSurveyData = this.state.survey === 'hra' ? this.state.hraSurveyStatusItems : this.state.chapsSurveyStatusItems;
        const isLastRow = index === currentSurveyData.length - 1;
        const rowClassName = isLastRow ? tableRowWithoutBorderCSS : tableRowCSS;

        return <div key={index} className={rowClassName}>
            <div className={smallColumnCSS}>
                {FONTS.reg_14(item.year)}
            </div>
            <div className={columnCSS}>
                {FONTS.reg_14(item.startedDate || "--/--/----")}
            </div>
            <div className={columnCSS}>
                {this.renderStatus(item.status, item)}
            </div>
            <div className={columnCSS}>
                {FONTS.reg_14(item.completedDate || "N/A")}
            </div>
            <div style={{width: 60, textAlign: "center", display: "flex", justifyContent: "center"}}>
                {this.renderThreeDotsButton(item)}
            </div>
        </div>;
    };

    private renderStatus = (status: string, item: SurveyStatus) => {
        let statusCSS;
        let statusText = "";
        const totalQuestions = this.state.survey === 'hra' ? this.totalHraQuestions : this.totalChapsQuestions;
        const questionCountToCheck = this.state.survey === 'hra' ? 31 : 20;
        if (item.deactivated) {
            statusText = 'Deactivated';
            statusCSS = statusDeactivatedCSS;
        } else if (+status >= questionCountToCheck) {
            statusText = 'Completed';
            statusCSS = statusCompletedCSS;
        } else if (+status > 0) {
            statusText = +status + "/" + totalQuestions;
            statusCSS = statusProgressCSS;
        }
        else {
            statusText = 'Not started';
            statusCSS = statusNotStartedCSS;
        }

        return <span className={statusCSS}>
            {statusText}
        </span>;
    };

    private renderThreeDotsButton = (item: SurveyStatus) => {
        return <button
            className={`three-dots-button ${threeDotsButtonCSS}`}
            onClick={(e) => this.handleThreeDotsClick(e, item)}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px'
            }}>
                <div style={{
                    width: '3px',
                    height: '3px',
                    backgroundColor: COLORS.neutral_text_dark,
                    borderRadius: '50%'
                }}></div>
                <div style={{
                    width: '3px',
                    height: '3px',
                    backgroundColor: COLORS.neutral_text_dark,
                    borderRadius: '50%'
                }}></div>
                <div style={{
                    width: '3px',
                    height: '3px',
                    backgroundColor: COLORS.neutral_text_dark,
                    borderRadius: '50%'
                }}></div>
            </div>
        </button>;
    };

    private renderModal = () => {
        if (!this.state.showModal || !this.state.selectedItem) return null;

        const {selectedItem} = this.state;
        const statusValue = parseFloat(selectedItem.status) || 0;
        const surveyStarted = statusValue > 0; // In progress or completed
        const showDownload = surveyStarted && Object.keys(selectedItem.answersJson || {}).length > 0;
        const showDeactivate = !statusValue || statusValue < 31;

        return <div
            className={`modal-content ${modalContentCSS}`}
            style={{
                left: this.state.modalPosition.x,
                top: this.state.modalPosition.y
            }}
            onClick={e => e.stopPropagation()}
        >
            {showDownload && (
                <div
                    className={modalOptionCSS}
                    onClick={async () => {
                        await this.handleDownload(selectedItem);
                        this.setState({showModal: false, selectedItem: null});
                    }}
                >
                    <span>
                        {FONTS.reg_14("Download")}
                    </span>
                </div>
            )}
            {showDeactivate && (<div
                    className={modalOptionCSS}
                    onClick={() => {
                        this.handleDeactivateToggle();
                    }}
                >
                <span>
                    {FONTS.reg_14(selectedItem.deactivated ? "Activate" : "Deactivate")}
                </span>
                </div>
            )}
        </div>;
    };

    private renderCalendarModal = () => {
        if (!this.state.showCalendar) return null;

        return <div className={calendarModalOverlayCSS} onClick={this.handleCalendarClose}>
            <div
                className={calendarModalContentCSS}
                style={{
                    left: this.state.calendarPosition.x,
                    top: this.state.calendarPosition.y
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{
                    marginBottom: 12,
                    fontSize: "12px",
                    fontWeight: "500",
                    fontFamily: "Inter-Regular",
                    color: COLORS.neutral_text_dark
                }}>
                    Select Anniversary Date
                </div>
                <Calendar
                    onFilter={this.handleCalendarDateSelect}
                    maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())}
                    initialDate={(() => {
                        const currentData = this.state.survey === 'hra' ? this.state.hraSurveyStatusItems : this.state.chapsSurveyStatusItems;
                        const anniversaryStr = currentData?.[0]?.anniversary;
                        if (!anniversaryStr || anniversaryStr === '--/--/----') return undefined;
                        const parsed = dayjs.utc(anniversaryStr, 'MM/DD/YYYY');
                        return parsed.isValid() ? parsed.toDate() : undefined;
                    })()}
                />
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={this.handleCalendarClose}
                        style={{
                            padding: "6px 12px",
                            backgroundColor: COLORS.grays_2,
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontFamily: "Inter-Regular"
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>;
    };


    private loadImageBase64 = async (path: string): Promise<string> => {
        // Small assets are inlined by Vite as data: URIs at build time; fetch()
        // on those is subject to the CSP connect-src directive (which allows
        // 'self' but not data:), so pass them through as-is instead.
        if (path.startsWith('data:'))
            return path;

        try {
            const response = await fetch(path);
            const blob = await response.blob();

            const reader = new FileReader();

            return await new Promise<string>((resolve, reject) => {
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            throw error;
        }
    };

    private handleDownload = async (item: SurveyStatus) => {
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 20;
            let yPosition = margin - 15;
            const lineHeight = 5;
            let currentPage = 1;

            const unitId = this.props.patient.unitId;
            const year = item.year || new Date().getFullYear();

            const answersCount = Object.keys(item.answersJson).length;
            const questionsPerPage = this.state.survey === 'hra' ? 9 : 7;
            const totalPages = Math.ceil(answersCount / questionsPerPage);

            const addHeader = async () => {
                const logoImg = await this.loadImageBase64(metro_plus_logo);
                doc.addImage(logoImg, 'PNG', margin, yPosition, 48, 25);

                doc.setFontSize(11);
                doc.setFont('helvetica', 'normal');
                doc.text(`Page ${currentPage} / ${totalPages}`, pageWidth - margin - 10, yPosition + 6);

                yPosition += 25;
            };

            const addSeparator = () => {
                doc.setDrawColor(190, 215, 255);
                doc.setLineWidth(0.2);
                doc.line(margin, yPosition, pageWidth - margin, yPosition);
                yPosition += 8;
            };

            const addNewPage = async () => {
                doc.addPage();
                currentPage++;
                yPosition = margin;
                await addHeader();
                yPosition += 10;
            };

            // Add header
            await addHeader();

            yPosition += 10;

            // Unit ID
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(`${unitId}`, margin, yPosition);
            yPosition += 12;

            // Title
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            const titleText = this.state.survey === 'hra' ? `HRA Report ${year}` : `CAHPS Report ${year}`;
            doc.text(titleText, margin, yPosition);
            yPosition += 12;

            // Last update and status
            const rawDate = item.completedDate ? new Date(item.completedDate) : new Date();
            const displayDate = rawDate.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
            }).replace(/,/g, ''); // "Jun 12 2025"

            const dateText = `Last update: ${displayDate}`;
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(dateText, margin, yPosition);

            const dateWidth = doc.getTextWidth(dateText);

            // Status
            const statusValue = parseFloat(item.status) || 0;
            const totalQuestions = this.state.survey === 'hra' ? 31 : this.totalChapsQuestions;
            const isCompleted = statusValue >= totalQuestions;
            const statusLabel = isCompleted ? 'Completed' : 'In progress';

            const iconX = margin + dateWidth + 7;
            const iconY = yPosition - 4;
            const labelX = iconX + 7;

            const statusIcon = await this.loadImageBase64(isCompleted ? check_icon : clock_icon);
            doc.addImage(statusIcon, 'PNG', iconX, iconY, 5, 5);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.text(statusLabel, labelX, yPosition);

            doc.setTextColor(0, 0, 0);
            yPosition += 20;

            const wrapText = (text: string, maxWidth: number): string[] => {
                return doc.splitTextToSize(text, maxWidth);
            };

            // Convert to array to process with index
            const answersArray = Object.values(item.answersJson)
                .map(data => data as { question: string; answer: string; questionUI: string })
                .filter(data => data && typeof data === 'object');

            // Add N/A for skipped questions if next question exists
            const processedAnswers = [];
            for (let i = 0; i < answersArray.length; i++) {
                const current = answersArray[i];
                processedAnswers.push(current);

                // Check if we should add N/A for skipped question
                const currentNum = parseInt(current.questionUI.match(/^(\d+)/)?.[1] || '0', 10);
                const nextAnswer = answersArray[i + 1];

                if (nextAnswer) {
                    const nextNum = parseInt(nextAnswer.questionUI.match(/^(\d+)/)?.[1] || '0', 10);

                    // Add N/A for missing questions between current and next
                    for (let missingNum = currentNum + 1; missingNum < nextNum; missingNum++) {
                        processedAnswers.push({
                            question: ``,
                            answer: 'N/A – question skipped',
                            questionUI: missingNum.toString()
                        });
                    }
                }
            }

            let questionsOnCurrentPage = 0;

            for (const data of processedAnswers) {
                const { question, answer, questionUI } = data;

                // Force page break if we've reached the limit OR if we're out of space
                if (questionsOnCurrentPage >= questionsPerPage || yPosition > pageHeight - 40) {
                    await addNewPage();
                    questionsOnCurrentPage = 0;
                }

                questionsOnCurrentPage++;

                // Check if this is a skipped question (N/A answer)
                const isSkippedQuestion = answer === 'N/A – question skipped';

                if (isSkippedQuestion) {
                    // For skipped questions, put everything on one line
                    // Question number in bold
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(12);
                    doc.text(`${questionUI}.`, margin, yPosition);

                    // Answer text in normal font like other answers
                    const questionNumWidth = doc.getTextWidth(`${questionUI}. `);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);
                    doc.setTextColor(60, 60, 60);
                    doc.text('N/A – question skipped', margin + questionNumWidth, yPosition);
                    doc.setTextColor(0, 0, 0);
                    yPosition += lineHeight + 2;
                } else {
                    // Regular question format
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(12);
                    const wrappedQuestion = wrapText(`${questionUI}. ${question}`, pageWidth - 2 * margin);
                    wrappedQuestion.forEach((line: string) => {
                        doc.text(line, margin, yPosition);
                        yPosition += lineHeight;
                    });

                    yPosition += 2;

                    // Answer
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);
                    doc.setTextColor(60, 60, 60);
                    const wrappedAnswer = wrapText(String(answer), pageWidth - 2 * margin);
                    wrappedAnswer.forEach((line: string) => {
                        doc.text(line, margin + 2, yPosition);
                        yPosition += lineHeight;
                    });

                    doc.setTextColor(0, 0, 0);
                }

                addSeparator();
            }

            const currentDate = new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
            });
            const safeDate = currentDate.replace(/[^a-zA-Z0-9]/g, '_');
            const filename = this.state.survey === 'hra' ? `HRA_Report_${unitId}_${year}_${safeDate}.pdf` : `CAHPS_Report_${unitId}_${year}_${safeDate}.pdf`;

            doc.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };
}